
<form [formGroup]="settingsForm" (ngSubmit)="onSave()">
  <div class="bg-white rounded-md shadow-sm">
    <div class="p-6 border-b bg-blue-50">
      <h2 class="text-xl font-bold text-blue-800"><i class="fa-solid fa-hospital mr-2"></i> ตั้งค่าคลินิก (Clinic Configuration)</h2>
      <p class="text-blue-600 mt-1 text-sm">จัดการข้อมูลทางการแพทย์และรูปแบบเอกสารใบรับรองแพทย์</p>
    </div>

    @if(isLoading()) {
      <div class="p-6 text-center text-gray-500"><i class="fa-solid fa-spinner animate-spin"></i> Loading...</div>
    } @else {
      <div class="p-6 space-y-8">
        
        <!-- 1. General Info -->
        <fieldset class="space-y-4">
            <legend class="text-lg font-bold text-gray-800 border-b pb-2 w-full mb-2">1. ข้อมูลใบอนุญาต (License Info)</legend>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">เลขที่ใบอนุญาตคลินิก (Clinic License No.)</label>
                    <input type="text" formControlName="licenseNumber" class="w-full border-gray-300 rounded-md text-sm">
                </div>
                <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">วันหมดอายุใบอนุญาต (Valid Until)</label>
                    <input type="date" formControlName="licenseValidUntil" class="w-full border-gray-300 rounded-md text-sm">
                </div>
                <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">เวลาทำการแพทย์ (Doctor Hours)</label>
                    <input type="text" formControlName="operatingHours" class="w-full border-gray-300 rounded-md text-sm" placeholder="เช่น 09:00 - 18:00">
                </div>
                <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">แพทย์ผู้รับผิดชอบหลัก (Default Doctor)</label>
                    <select formControlName="defaultDoctorId" class="w-full border-gray-300 rounded-md text-sm">
                        <option [ngValue]="null">-- ไม่ระบุ --</option>
                        @for(doc of doctors(); track doc.uid) {
                            <option [value]="doc.uid">{{ doc.name }} ({{ doc.role }})</option>
                        }
                    </select>
                </div>
            </div>
            
            <!-- Profession Type Selection -->
            <div class="mt-4">
                <label class="block text-sm font-medium text-gray-700 mb-2">ประเภทผู้ประกอบวิชาชีพ (Profession Type)</label>
                <p class="text-xs text-gray-500 mb-3">เลือกประเภทที่จะแสดงเครื่องหมายถูกในใบรับรองแพทย์ (ภท.32)</p>
                <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    @for(prof of professions; track prof.id) {
                        <label class="flex items-center p-3 border rounded-lg hover:bg-slate-50 cursor-pointer transition-colors"
                               [class.border-blue-500]="settingsForm.value.professionType === prof.id"
                               [class.bg-blue-50]="settingsForm.value.professionType === prof.id">
                            <input type="radio" formControlName="professionType" [value]="prof.id" class="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500">
                            <span class="ml-2 text-sm text-gray-700 font-medium">{{ prof.label }}</span>
                        </label>
                    }
                </div>
            </div>

            <div class="flex items-center pt-2">
                <input type="checkbox" formControlName="enableTelemedAutoApprove" id="autoApprove" class="h-4 w-4 text-blue-600 rounded border-gray-300">
                <label for="autoApprove" class="ml-2 text-sm text-gray-700">เปิดระบบคัดกรองอัตโนมัติ (AI Auto-Screening)</label>
            </div>
        </fieldset>
        
        <!-- NEW: Watermark Settings -->
        <fieldset class="space-y-4">
             <legend class="text-lg font-bold text-gray-800 border-b pb-2 w-full mb-2">2. การยืนยันตัวตน (KYC Settings)</legend>
             <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">ข้อความคาดลายน้ำบนบัตรประชาชน (ID Card Watermark Text)</label>
                <input type="text" formControlName="watermarkText" class="w-full border-gray-300 rounded-md text-sm" placeholder="ใช้สำหรับขอใบสั่งจ่ายสมุนไพรควบคุม (กัญชา) เท่านั้น">
                <p class="text-xs text-gray-500 mt-1">ข้อความนี้จะถูกพิมพ์คาดลงบนรูปบัตรประชาชนที่สแกนเข้าระบบโดยอัตโนมัติ</p>
             </div>
        </fieldset>

        <!-- 3. Electronic Signature -->
        <fieldset class="space-y-4">
            <legend class="text-lg font-bold text-gray-800 border-b pb-2 w-full mb-2">3. ลายเซ็นแพทย์ (Electronic Signature)</legend>
            <p class="text-xs text-gray-500 -mt-3 mb-4">เซ็นชื่อในกรอบด้านล่างเพื่อใช้แสดงในใบรับรองแพทย์และแบบ ภท. โดยอัตโนมัติ</p>

            <div class="flex flex-col md:flex-row gap-6 items-start">
                <!-- Signature Pad -->
                <div>
                    <label class="block text-sm font-bold text-gray-700 mb-2">เซ็นชื่อที่นี่ (Sign Here):</label>
                    <div class="border-2 border-dashed border-gray-300 rounded-lg bg-gray-50 relative touch-none">
                        <canvas #signaturePad width="400" height="150" class="cursor-crosshair w-full max-w-[400px]"></canvas>
                        <div class="absolute bottom-2 right-2 flex gap-2">
                            <button type="button" (click)="clearSignature()" class="text-xs bg-white border border-gray-300 px-2 py-1 rounded text-gray-600 hover:bg-gray-100">ล้าง (Clear)</button>
                            <button type="button" (click)="saveSignatureFromPad()" class="text-xs bg-blue-600 text-white px-2 py-1 rounded hover:bg-blue-700">ยืนยัน (Use)</button>
                        </div>
                    </div>
                    <p class="text-[10px] text-gray-400 mt-1">* ใช้นิ้ว หรือ ปากกา (Stylus) วาดลายเซ็น</p>
                </div>

                <!-- Preview Current Signature -->
                <div class="flex-grow">
                    <label class="block text-sm font-bold text-gray-700 mb-2">ลายเซ็นปัจจุบัน (Current Signature):</label>
                    <div class="h-[150px] w-full max-w-[300px] border border-gray-200 rounded-lg flex items-center justify-center bg-white overflow-hidden relative">
                        @if(signaturePreview()) {
                            <img [src]="signaturePreview()" class="max-w-full max-h-full">
                            <button type="button" (click)="removeImage('doctorSignatureUrl')" class="absolute top-1 right-1 text-red-500 hover:text-red-700 bg-white rounded-full p-1 shadow">
                                <i class="fa-solid fa-trash"></i>
                            </button>
                        } @else {
                            <span class="text-gray-400 text-sm">ยังไม่มีลายเซ็น</span>
                        }
                    </div>
                    <!-- File Upload Fallback -->
                    <div class="mt-2">
                        <label class="text-xs text-gray-500 mr-2">หรืออัพโหลดรูปภาพ:</label>
                        <input type="file" (change)="onFileSelected($event, 'doctorSignatureUrl')" accept="image/*" class="text-xs text-gray-500">
                    </div>
                </div>
            </div>
        </fieldset>

        <!-- 4. Document Template -->
        <fieldset class="space-y-4">
            <legend class="text-lg font-bold text-gray-800 border-b pb-2 w-full mb-2">4. รูปแบบเอกสาร (Template)</legend>
            
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <!-- Header Text -->
                <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">หัวข้อเอกสาร (Header)</label>
                    <input type="text" formControlName="headerText" class="w-full border-gray-300 rounded-md text-sm">
                </div>
                <!-- Address -->
                <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">ที่อยู่บนใบรับรอง (Address)</label>
                    <textarea formControlName="addressText" rows="1" class="w-full border-gray-300 rounded-md text-sm" placeholder="ถ้าว่างจะใช้ที่อยู่ร้านค้า"></textarea>
                </div>
            </div>

            <!-- Style Selector -->
            <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">ตราสัญลักษณ์ (Emblem)</label>
                <div class="grid grid-cols-3 gap-3 max-w-lg">
                    <label class="cursor-pointer">
                        <input type="radio" formControlName="certificateHeaderStyle" value="GARUDA" class="peer sr-only">
                        <div class="p-3 border-2 rounded-lg text-center peer-checked:border-blue-500 peer-checked:bg-blue-50 hover:bg-gray-50 transition-all">
                            <i class="fa-solid fa-feather-pointed text-2xl mb-2 text-gray-600 peer-checked:text-blue-600"></i>
                            <div class="text-xs font-bold">ตราครุฑ (ทางการ)</div>
                        </div>
                    </label>
                    <label class="cursor-pointer">
                        <input type="radio" formControlName="certificateHeaderStyle" value="LOGO" class="peer sr-only">
                        <div class="p-3 border-2 rounded-lg text-center peer-checked:border-blue-500 peer-checked:bg-blue-50 hover:bg-gray-50 transition-all">
                            <i class="fa-solid fa-image text-2xl mb-2 text-gray-600 peer-checked:text-blue-600"></i>
                            <div class="text-xs font-bold">โลโก้คลินิก</div>
                        </div>
                    </label>
                    <label class="cursor-pointer">
                        <input type="radio" formControlName="certificateHeaderStyle" value="NONE" class="peer sr-only">
                        <div class="p-3 border-2 rounded-lg text-center peer-checked:border-blue-500 peer-checked:bg-blue-50 hover:bg-gray-50 transition-all">
                            <i class="fa-solid fa-ban text-2xl mb-2 text-gray-600 peer-checked:text-blue-600"></i>
                            <div class="text-xs font-bold">ไม่มีสัญลักษณ์</div>
                        </div>
                    </label>
                </div>
            </div>

            <!-- Logo Upload -->
            @if(settingsForm.value.certificateHeaderStyle === 'LOGO') {
                <div class="bg-slate-50 p-4 rounded-lg border border-dashed border-slate-300 animate-fade-in-fast">
                    <label class="block text-sm font-medium text-gray-700 mb-2">อัพโหลดโลโก้ (Clinic Logo)</label>
                    <div class="flex items-center gap-4">
                        @if(logoPreview()) {
                            <div class="relative w-20 h-20 border bg-white rounded-md overflow-hidden flex items-center justify-center">
                                <img [src]="logoPreview()" class="max-w-full max-h-full object-contain">
                                <button type="button" (click)="removeImage('certificateLogoUrl')" class="absolute top-0 right-0 bg-red-500 text-white w-5 h-5 flex items-center justify-center text-xs">×</button>
                            </div>
                        }
                        <input type="file" (change)="onFileSelected($event, 'certificateLogoUrl')" accept="image/*" class="text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100">
                    </div>
                </div>
            }

            <!-- Background Template -->
            <div class="pt-4 border-t">
                <div class="flex items-center justify-between mb-2">
                    <span class="text-sm font-medium">ใช้รูปพื้นหลังสำเร็จรูป (A4 Background Template)</span>
                    <label class="relative inline-flex items-center cursor-pointer">
                        <input type="checkbox" formControlName="useCustomTemplate" class="sr-only peer">
                        <div class="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                </div>
                <p class="text-xs text-gray-500 mb-4">สำหรับคลินิกที่มีกระดาษหัวจดหมาย หรือไฟล์รูปภาพที่มีกรอบ/ลายน้ำอยู่แล้ว</p>

                @if(settingsForm.value.useCustomTemplate) {
                    <div class="bg-slate-50 p-4 rounded-lg border border-dashed border-slate-300">
                        <div class="flex flex-col gap-3">
                            <input type="file" (change)="onFileSelected($event, 'certificateBgUrl')" accept="image/*" class="text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100">
                            @if(bgPreview()) {
                                <div class="relative w-full h-48 border bg-white rounded-md overflow-hidden flex items-center justify-center bg-gray-100">
                                    <img [src]="bgPreview()" class="max-w-full max-h-full object-contain">
                                    <button type="button" (click)="removeImage('certificateBgUrl')" class="absolute top-2 right-2 bg-red-600 text-white px-2 py-1 rounded text-xs shadow">ลบรูป</button>
                                </div>
                            }
                        </div>
                    </div>
                }
            </div>
        </fieldset>

      </div>
    }

    <div class="p-6 bg-gray-50 rounded-b-md flex justify-end items-center gap-4">
      @if (statusMessage(); as msg) {
        <div class="text-sm font-semibold" [class]="msg.type === 'success' ? 'text-green-600' : 'text-red-600'">{{ msg.text }}</div>
      }
      <button type="submit" [disabled]="settingsForm.invalid || isSaving() || isLoading()" class="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:bg-slate-400 disabled:cursor-not-allowed">
        @if (isSaving()) { <i class="fa-solid fa-spinner animate-spin"></i><span>Saving...</span> } 
        @else { <i class="fa-solid fa-save"></i><span>Save Clinic Settings</span> }
      </button>
    </div>
  </div>
</form>
